import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { getNotificationStyle } from '../common/notification';
import {
    DeleteMasterRateRangeGroupByIdService,
    DeleteMasterRateRangeStationByIdService,
    DeleteMasterStationByIdService,
    DeleteMasterStationGroupByIdService,
    GetMasterRateRangeGroupListByProductService,
    GetMasterRateRangeStationListByProductService,
    GetMasterStationByIdService,
    GetMasterStationGroupByIdService,
    GetMasterStationGroupListByProductService,
    GetMasterStationListByProductService,
    SaveMasterRateRangeGroupBulkService,
    SaveMasterRateRangeStationBulkService,
    SaveMasterStationBulkService,
    SaveMasterStationGroupBulkService,
    UpdateMasterRateRangeGroupService,
    UpdateMasterRateRangeStationService,
    UpdateMasterStationGroupService,
    UpdateMasterStationService,
    UploadMasterStationFileService,
} from '../services/manual-waybill-charges-service';
import { GetActiveProductsService } from '../services/manual-waybill-service';
import { TWO_HUNDRED_SUCCESS_CODE } from '../utils/app-common';
import { responseData } from './common';
import { IFileUpload } from '../model/CommonModel';

export type IMasterStation = {
    id?: number;
    orgCode?: string;
    orgCityId?: number;
    orgCityCode?: string;
    orgCityName?: string;
    orgCityNameAr?: string;
    orgCountryId?: number;
    orgCountryName?: string;
    orgCountryNameAr?: string;
    destCode?: string;
    destCityId?: number;
    destCityCode?: string;
    destCityName?: string;
    destCityNameAr?: string;
    destCountryId?: number;
    destCountryName?: string;
    destCountryNameAr?: string;
    basePrice?: number;
    agreementProductId?: number;
    productId?: number;
    product?: string;
    createdDate?: number;
    statusId?: number;
};

export type IMasterStationGroup = {
    id?: number;
    fromCityGroup?: number;
    fromCityGroupCode?: string;
    toCityGroup?: number;
    toCityGroupCode?: string;
    basePrice?: number;
    agreementProductId?: number;
    productId?: number;
    product?: string;
    createdDate?: number;
    statusId?: number;
};

export type IMasterRateRangeGroup = {
    id?: number;
    agreementProductId?: number;
    agreementProductUnitId?: number;
    agreementProductUnit?: string;
    from?: number;
    to?: number | null;
    fractionVal?: number;
    rate?: number;
    stationGroupId?: number;
    stationGroup?: string;
    productId?: number;
    product?: string;
    createdDate?: number;
    statusId?: number;
    status?: string;
};

export type IMasterRateRangeStation = {
    id?: number;
    agreementProductId?: number;
    agreementProductUnitId?: number;
    agreementProductUnit?: string;
    from?: number;
    to?: number | null;
    fractionVal?: number;
    rate?: number;
    orgCityId?: number;
    orgCityCode?: string;
    orgCityName?: string;
    orgCityNameAr?: string;
    orgCountryId?: number;
    orgCountryName?: string;
    orgCountryNameAr?: string;
    destCityId?: number;
    destCityCode?: string;
    destCityName?: string;
    destCityNameAr?: string;
    destCountryId?: number;
    destCountryName?: string;
    destCountryNameAr?: string;
    productId?: number;
    product?: string;
    createdDate?: number;
    statusId?: number;
    status?: string;
};

export type IActiveProduct = {
    id?: number;
    name?: string;
    status?: string;
};

interface IManualWaybillChargesState {
    activeProductList: IActiveProduct[];

    masterStationList: IMasterStation[];
    masterStationDetails: IMasterStation;
    masterStationResponse: any;
    masterStationGroupList: IMasterStationGroup[];
    masterStationGroupDetails: IMasterStationGroup;
    masterStationGroupResponse: any;
    selectedStationProductId?: number;
    selectedStationGroupProductId?: number;

    masterRateRangeGroupList: IMasterRateRangeGroup[];
    masterRateRangeGroupResponse: any;
    selectedRateRangeGroupProductId?: number;

    masterRateRangeStationList: IMasterRateRangeStation[];
    masterRateRangeStationResponse: any;
    selectedRateRangeStationProductId?: number;

    GetActiveProductListAction: () => Promise<void>;

    GetMasterStationListAction: (productId: number) => Promise<void>;
    GetMasterStationByIdAction: (id: number) => Promise<void>;
    SaveMasterStationBulkAction: (data: { agreementStationsPriceList: any[] }, productId: number) => Promise<void>;
    UpdateMasterStationAction: (data: IMasterStation, id: number) => Promise<void>;
    DeleteMasterStationAction: (id: number) => Promise<void>;
    UploadMasterStationFileAction: (fileObject: Partial<IFileUpload>, productId: number) => Promise<void>;

    GetMasterStationGroupListAction: (productId: number) => Promise<void>;
    GetMasterStationGroupByIdAction: (id: number) => Promise<void>;
    SaveMasterStationGroupBulkAction: (data: { agreementStationGroupPriceDtoList: any[] }, productId: number) => Promise<void>;
    UpdateMasterStationGroupAction: (data: IMasterStationGroup, id: number) => Promise<void>;
    DeleteMasterStationGroupAction: (id: number) => Promise<void>;

    GetMasterRateRangeGroupListAction: (productId: number) => Promise<void>;
    SaveMasterRateRangeGroupBulkAction: (data: { agreementRateRangeDtoList: any[] }, productId: number) => Promise<void>;
    UpdateMasterRateRangeGroupAction: (data: IMasterRateRangeGroup, id: number) => Promise<void>;
    DeleteMasterRateRangeGroupAction: (id: number) => Promise<void>;

    GetMasterRateRangeStationListAction: (productId: number) => Promise<void>;
    SaveMasterRateRangeStationBulkAction: (data: { agreementRateRangeDtoList: any[] }, productId: number) => Promise<void>;
    UpdateMasterRateRangeStationAction: (data: IMasterRateRangeStation, id: number) => Promise<void>;
    DeleteMasterRateRangeStationAction: (id: number) => Promise<void>;

    ResetManualWaybillChargesStoreAction: () => void;
}

const initialValue = {
    activeProductList: [],

    masterStationList: [],
    masterStationDetails: {},
    masterStationResponse: {},
    masterStationGroupList: [],
    masterStationGroupDetails: {},
    masterStationGroupResponse: {},
    selectedStationProductId: undefined as number | undefined,
    selectedStationGroupProductId: undefined as number | undefined,
    masterRateRangeGroupList: [],
    masterRateRangeGroupResponse: {},
    selectedRateRangeGroupProductId: undefined as number | undefined,
    masterRateRangeStationList: [],
    masterRateRangeStationResponse: {},
    selectedRateRangeStationProductId: undefined as number | undefined,
};

const ManualWaybillChargesStore = create<IManualWaybillChargesState>()(
    devtools((set, get) => ({
        ...initialValue,

        GetActiveProductListAction: async () => {
            const response = await GetActiveProductsService();
            set({ activeProductList: responseData(response?.data) || [] });
        },

        GetMasterStationListAction: async (productId: number) => {
            set({ selectedStationProductId: productId });
            const response = await GetMasterStationListByProductService(productId);
            set({ masterStationList: responseData(response?.data) || [] });
        },

        GetMasterStationByIdAction: async (id: number) => {
            const response = await GetMasterStationByIdService(id);
            set({ masterStationDetails: responseData(response?.data) || {} });
        },

        SaveMasterStationBulkAction: async (data, productId) => {
            const response = await SaveMasterStationBulkService(data, productId);
            set({ masterStationResponse: response });
            if (response?.status === TWO_HUNDRED_SUCCESS_CODE) {
                getNotificationStyle({ ...response, statusText: 'Saved Successfully!' });
                get().GetMasterStationListAction(productId);
            }
        },

        UpdateMasterStationAction: async (data, id) => {
            const response = await UpdateMasterStationService(data, id);
            set({ masterStationResponse: response });
            if (response?.status === TWO_HUNDRED_SUCCESS_CODE) {
                getNotificationStyle({ ...response, statusText: 'Updated Successfully!' });
                get().GetMasterStationListAction(get().selectedStationProductId!);
            }
        },

        DeleteMasterStationAction: async (id) => {
            const response = await DeleteMasterStationByIdService(id);
            if (response?.status === TWO_HUNDRED_SUCCESS_CODE) {
                getNotificationStyle({ ...response, statusText: 'Deleted Successfully!' });
                get().GetMasterStationListAction(get().selectedStationProductId!);
            }
        },

        UploadMasterStationFileAction: async (fileObject, productId) => {
            const response = await UploadMasterStationFileService(fileObject, productId);
            if (response?.status === TWO_HUNDRED_SUCCESS_CODE) {
                getNotificationStyle({ ...response, statusText: 'Uploaded Successfully!' });
                get().GetMasterStationListAction(productId);
            }
        },

        GetMasterStationGroupListAction: async (productId: number) => {
            set({ selectedStationGroupProductId: productId });
            const response = await GetMasterStationGroupListByProductService(productId);
            set({ masterStationGroupList: responseData(response?.data) || [] });
        },

        GetMasterStationGroupByIdAction: async (id: number) => {
            const response = await GetMasterStationGroupByIdService(id);
            set({ masterStationGroupDetails: responseData(response?.data) || {} });
        },

        SaveMasterStationGroupBulkAction: async (data, productId) => {
            const response = await SaveMasterStationGroupBulkService(data, productId);
            set({ masterStationGroupResponse: response });
            if (response?.status === TWO_HUNDRED_SUCCESS_CODE) {
                getNotificationStyle({ ...response, statusText: 'Saved Successfully!' });
                get().GetMasterStationGroupListAction(productId);
            }
        },

        UpdateMasterStationGroupAction: async (data, id) => {
            const response = await UpdateMasterStationGroupService(data, id);
            set({ masterStationGroupResponse: response });
            if (response?.status === TWO_HUNDRED_SUCCESS_CODE) {
                getNotificationStyle({ ...response, statusText: 'Updated Successfully!' });
                get().GetMasterStationGroupListAction(get().selectedStationGroupProductId!);
            }
        },

        DeleteMasterStationGroupAction: async (id) => {
            const response = await DeleteMasterStationGroupByIdService(id);
            if (response?.status === TWO_HUNDRED_SUCCESS_CODE) {
                getNotificationStyle({ ...response, statusText: 'Deleted Successfully!' });
                get().GetMasterStationGroupListAction(get().selectedStationGroupProductId!);
            }
        },

        GetMasterRateRangeGroupListAction: async (productId: number) => {
            set({ selectedRateRangeGroupProductId: productId });
            const response = await GetMasterRateRangeGroupListByProductService(productId);
            set({ masterRateRangeGroupList: responseData(response?.data) || [] });
        },

        SaveMasterRateRangeGroupBulkAction: async (data, productId) => {
            const response = await SaveMasterRateRangeGroupBulkService(data, productId);
            set({ masterRateRangeGroupResponse: response });
            if (response?.status === TWO_HUNDRED_SUCCESS_CODE) {
                getNotificationStyle({ ...response, statusText: 'Saved Successfully!' });
                get().GetMasterRateRangeGroupListAction(productId);
            }
        },

        UpdateMasterRateRangeGroupAction: async (data, id) => {
            const response = await UpdateMasterRateRangeGroupService(data, id);
            set({ masterRateRangeGroupResponse: response });
            if (response?.status === TWO_HUNDRED_SUCCESS_CODE) {
                getNotificationStyle({ ...response, statusText: 'Updated Successfully!' });
                get().GetMasterRateRangeGroupListAction(get().selectedRateRangeGroupProductId!);
            }
        },

        DeleteMasterRateRangeGroupAction: async (id) => {
            const response = await DeleteMasterRateRangeGroupByIdService(id);
            if (response?.status === TWO_HUNDRED_SUCCESS_CODE) {
                getNotificationStyle({ ...response, statusText: 'Deleted Successfully!' });
                get().GetMasterRateRangeGroupListAction(get().selectedRateRangeGroupProductId!);
            }
        },

        GetMasterRateRangeStationListAction: async (productId: number) => {
            set({ selectedRateRangeStationProductId: productId });
            const response = await GetMasterRateRangeStationListByProductService(productId);
            set({ masterRateRangeStationList: responseData(response?.data) || [] });
        },

        SaveMasterRateRangeStationBulkAction: async (data, productId) => {
            const response = await SaveMasterRateRangeStationBulkService(data, productId);
            set({ masterRateRangeStationResponse: response });
            if (response?.status === TWO_HUNDRED_SUCCESS_CODE) {
                getNotificationStyle({ ...response, statusText: 'Saved Successfully!' });
                get().GetMasterRateRangeStationListAction(productId);
            }
        },

        UpdateMasterRateRangeStationAction: async (data, id) => {
            const response = await UpdateMasterRateRangeStationService(data, id);
            set({ masterRateRangeStationResponse: response });
            if (response?.status === TWO_HUNDRED_SUCCESS_CODE) {
                getNotificationStyle({ ...response, statusText: 'Updated Successfully!' });
                get().GetMasterRateRangeStationListAction(get().selectedRateRangeStationProductId!);
            }
        },

        DeleteMasterRateRangeStationAction: async (id) => {
            const response = await DeleteMasterRateRangeStationByIdService(id);
            if (response?.status === TWO_HUNDRED_SUCCESS_CODE) {
                getNotificationStyle({ ...response, statusText: 'Deleted Successfully!' });
                get().GetMasterRateRangeStationListAction(get().selectedRateRangeStationProductId!);
            }
        },

        ResetManualWaybillChargesStoreAction: () => {
            set({ ...initialValue });
        },
    }))
);

export const useManualWaybillChargesStore = ManualWaybillChargesStore;
